import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import Debug from 'debug';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import * as fromActions from '../actions/users.management.actions';
import * as fromOrganizationActions from '../actions/organization.actions';
import { GetUserDto } from '../dtos/getUserDto.type';
import { UsersManagementService } from '../services/users.management.service';

const debug = Debug('modeso:aumico-lib-users-fe:UsersManagementEffects');

@Injectable()
export class UsersManagementEffects {
    constructor(
        private actions$: Actions,
        private service: UsersManagementService,
        private store: Store,
    ) {}

    getUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.userManagement.getUsersForAdmin.type),
            mergeMap(() => {
                return this.service.getAllUsers$().pipe(
                    map((response: GetUserDto[]) => {
                        debug(response);
                        return fromActions.userManagement.getUsersForAdminSuccess({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.userManagement.getUsersForAdminFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    blockUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.userManagement.blockUser.type),
            mergeMap(({ payload }) => {
                return this.service.blockUser$(payload).pipe(
                    map(() => {
                        return fromActions.userManagement.blockUserSuccess({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.userManagement.blockUserFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );
    unblockUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.userManagement.unblockUser.type),
            mergeMap(({ payload }) => {
                return this.service.unblockUser$(payload).pipe(
                    map(() => {
                        return fromActions.userManagement.unblockUserSuccess({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.userManagement.unblockUserFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    promoteAdmin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.userManagement.promoteUserToAdmin.type),
            mergeMap(({ payload }) => {
                return this.service.promoteUser$(payload).pipe(
                    map(() => {
                        return fromActions.userManagement.promoteUserToAdminSuccess({ payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.userManagement.promoteUserToAdminFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );
    demoteUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.userManagement.demoteUserFromAdmin.type),
            mergeMap(({ payload }) =>
                this.service.demoteUser$(payload).pipe(
                    map(() => fromActions.userManagement.demoteUserFromAdminSuccess({ payload })),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.userManagement.demoteUserFromAdminFailure({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    changeUserRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.userManagement.changeUserRole.type),
            mergeMap(({ userId, newRole }) =>
                this.service.changeUserRole$(userId, newRole).pipe(
                    map((user) => fromActions.userManagement.changeUserRoleSuccess({ payload: user })),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.userManagement.changeUserRoleFailure({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    deleteUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.userManagement.deleteUser.type),
            mergeMap(({ payload }) =>
                this.service.deleteUser$(payload).pipe(
                    map(() => fromActions.userManagement.deleteUserSuccess({ payload })),
                    tap(() => {
                        // Dispatch action to update the search index after successful deletion
                        this.store.dispatch(fromOrganizationActions.createIndexAction.do());
                    }),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.userManagement.deleteUserFailure({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    errorOnUserManagement$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fromActions.userManagement.getUsersForAdminFailure.type),
                tap((action: fromActions.ActionWithPayload<HttpErrorResponse>) => {
                    this.handleOnLoadAllErrors(action.payload);
                }),
            ),
        { dispatch: false },
    );

    handleOnLoadAllErrors(error: HttpErrorResponse): HttpErrorResponse {
        debug('handleOnLoadAllErrors');
        debug(error);
        return error;
    }
}
